import { type InfiniteData } from '@tanstack/react-query';

import { type AgentSessionMessageResponse, type ListAgentSessionMessagesResponse } from '@air/server-sdk';

/** Latest page only — live catch-up must not refetch every history page the infinite query has loaded. */
export const WORKSPACE_AGENT_LIVE_MESSAGE_CATCH_UP_LIMIT = 10;

export type WorkspaceAgentSessionMessagesInfiniteData = InfiniteData<ListAgentSessionMessagesResponse, string>;

export type MergeWorkspaceAgentCatchUpMessagesIntoFirstPageParams = {
  current: WorkspaceAgentSessionMessagesInfiniteData | undefined;
  latestMessages: AgentSessionMessageResponse[];
};

/**
 * Appends missed rows onto cached page 0 so a session switch still hydrates them. Ids already on
 * any loaded page are left alone — catch-up is a gap-fill, not a rewrite of WSS or history pages.
 */
export const mergeWorkspaceAgentCatchUpMessagesIntoFirstPage = ({
  current,
  latestMessages,
}: MergeWorkspaceAgentCatchUpMessagesIntoFirstPageParams): WorkspaceAgentSessionMessagesInfiniteData | undefined => {
  const firstPage = current?.pages[0];

  if (!current || !firstPage) {
    return current;
  }

  const existingIds = new Set<string>();

  for (const historyPage of current.pages) {
    for (const message of historyPage.data) {
      existingIds.add(message.id);
    }
  }

  const newMessages = latestMessages.filter((message) => !existingIds.has(message.id));

  if (newMessages.length === 0) {
    return current;
  }

  return {
    ...current,
    pages: [{ ...firstPage, data: [...firstPage.data, ...newMessages] }, ...current.pages.slice(1)],
  };
};
